<script setup lang="ts">
import Button from '@ui/components/button/Index.vue'
import Icon from '@ui/components/icon/Index.vue'
import Heading from '@ui/components/heading/Index.vue'
import Overlay from '@ui/components/overlay/Index.vue'

import { computed, ref, toRefs } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

import { useSize } from '@ui/composables/dialog/useSize'

import type { Color } from '@ui/types/base/color'
import type { Padding, Size } from '@ui/types/dialog'

const emit = defineEmits<{
  (event: 'opened' | 'closed'): void
  (event: 'update:modelValue', value: boolean): void
}>()

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    padding?: Padding
    size?: Size
    titleColor?: Color
    zIndex?: string
    overlayZIndex?: string
    disableCloseButton?: boolean
    hideCloseButton?: boolean
    hideTitle?: boolean
    bgAccent?: boolean
    fixedCloseButton?: boolean
  }>(),
  {
    modelValue: false,
    padding: 'lg',
    size: 'md',
    titleColor: 'primary',
    zIndex: 'z-40',
    overlayZIndex: 'z-30',
    disableCloseButton: false,
    hideCloseButton: false,
    hideTitle: false,
    bgAccent: false,
    fixedCloseButton: false
  }
)

const { modelValue, padding, size, titleColor, disableCloseButton, hideCloseButton, hideTitle } = toRefs(props)

const dialogRef = ref<HTMLElement | null>(null)

const isOpen = computed({
  get: () => modelValue.value,
  set: (value) => {
    emit('update:modelValue', value)
  }
})

const closeDialog = () => {
  isOpen.value = false
}
</script>

<template>
  <TransitionRoot
    :show="isOpen"
    as="template"
    @after-leave="() => emit('closed')"
  >
    <Dialog
      ref="dialogRef"
      as="div"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <Overlay :z-index="overlayZIndex" />
      </TransitionChild>

      <div :class="['fixed inset-0 overflow-y-auto m-4', props.zIndex]">
        <div class="flex min-h-full items-center justify-center text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[
                useTailwindClasses([usePadding(padding), useSize(size)]),
                props.zIndex,
                'dialog-panel w-full transform rounded-xl bg-white text-left align-middle shadow-xl transition-all space-y-5'
              ]"
            >
              <div
                v-if="!hideTitle"
                :class="[
                  'flex items-center min-h-[42px]',
                  hideCloseButton ? 'justify-center' : 'space-x-5 justify-between'
                ]"
              >
                <div
                  :class="['flex-1 flex items-center', hideCloseButton ? 'justify-center' : '']"
                  :style="
                    !hideCloseButton && fixedCloseButton
                      ? 'margin-left: 36px; max-width: calc(100% - 72px);'
                      : undefined
                  "
                >
                  <Heading
                    size="xl"
                    weight="bold"
                    :class="['font-montserrat', useTailwindClasses([useTextColor(titleColor)])]"
                  >
                    <slot name="title" />
                  </Heading>
                </div>
                <div
                  v-if="!hideCloseButton"
                  :class="['flex items-center', { 'fixed right-4 top-4': fixedCloseButton }]"
                >
                  <Button
                    variant="text"
                    icon
                    :disabled="disableCloseButton"
                    @click="closeDialog"
                  >
                    <Icon
                      :icon="XMarkIcon"
                      size="lg"
                    />
                  </Button>
                </div>
              </div>

              <!-- Show only close button when title is hidden but close button is not -->
              <div
                v-else-if="hideTitle && !hideCloseButton"
                class="flex justify-end min-h-[42px] items-center"
              >
                <div :class="['flex items-center', { 'fixed right-4 top-4': fixedCloseButton }]">
                  <Button
                    variant="text"
                    icon
                    size="lg"
                    :disabled="disableCloseButton"
                    @click="closeDialog"
                  >
                    <Icon
                      :icon="XMarkIcon"
                      size="lg"
                    />
                  </Button>
                </div>
              </div>

              <div :class="{ 'p-5 bg-[#f7fafc] border rounded-xl': bgAccent }">
                <slot />
              </div>

              <div class="actions flex justify-center space-x-2">
                <slot name="actions" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
